import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { Check } from 'lucide-react';

export interface SelectionCardProps {
  id: string;
  /** The chip glyph, 20px. Pass a lucide icon with `aria-hidden`. */
  icon: ReactNode;
  label: string;
  /** First-person rows describing what this choice commits the parent to. Up to three. */
  checks: readonly string[];
  selected: boolean;
  onSelect: (id: string) => void;
  /**
   * Tab stop for roving focus inside the parent `radiogroup`: the group sets
   * 0 on the checked (or first) card and -1 on the rest, so Tab enters the
   * group once. Arrow-key movement between cards is the group's
   * responsibility, not this component's. Defaults to 0 for standalone use.
   */
  tabIndex?: number;
  /**
   * Forwarded to the card element so the parent radiogroup can move focus to
   * it programmatically (arrow keys). React 19 treats `ref` as a plain prop
   * on function components — no `forwardRef` needed.
   */
  ref?: Ref<HTMLDivElement>;
}

/**
 * The Figma "Selection Card" (Cards page, 10707:165393): a selectable option
 * for "pick one of these" choices where each option has to say what it
 * commits the user to. First used on the onboarding fork (YAR-438).
 *
 * Geometry from "Fork / default": white card, 1px `border/default` (#d1d1d1,
 * token `border-strong`) at 12px radius, 16px padding, 12px column gap.
 * Header row: a 40px outline chip (`border/neutral/default-subtle`, the
 * plain `border` token) with a 20px glyph · the label at body-lg 20/28 · a
 * 28px radio on the same `border/default`. Check rows: 20px `icon/primary`
 * check + body-md 16/24 caption text, 8px apart. Selected ("Fork / default /
 * Set up my family"): the border stays 1px but turns `border/primary/default`
 * (#1f372d — `primary-hover`, the same token the Button family uses for that
 * Figma colour), the card takes the `hover/default` effect
 * (`shadow-hover-primary`), and the radio fills with a 12px `primary` dot
 * inside the same primary outline. QA measured the first cut against Figma
 * (YAR-438): 2px/#264133 border, no lift, 14px dot, grey checks — every one
 * of those is now the Figma value.
 *
 * The whole card is the control — `role="radio"` on the card, not on the
 * inner circle — so the hit area is the card. The parent supplies
 * `role="radiogroup"`.
 *
 * Accessibility: name and description are wired explicitly via
 * `aria-labelledby`/`aria-describedby` rather than `aria-label` or plain
 * text content, because `role="radio"` makes the card's subtree
 * presentational to assistive tech — descendants (the label span, the check
 * list) are not reliably announced on their own. `aria-labelledby` points at
 * the label span (the accessible name), `aria-describedby` at the checks
 * list (the accessible description), so both stay deterministic across AT
 * regardless of how the subtree renders.
 */
export function SelectionCard({
  id,
  icon,
  label,
  checks,
  selected,
  onSelect,
  tabIndex = 0,
  ref,
}: SelectionCardProps) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onSelect(id);
    }
  };
  return (
    <div
      ref={ref}
      role="radio"
      aria-checked={selected}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-checks`}
      tabIndex={tabIndex}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onSelect(id)}
      onKeyDown={onKeyDown}
      className={[
        'w-full cursor-pointer rounded-xl bg-white flex flex-col gap-3 text-left',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        'border p-4',
        selected
          ? 'border-primary-hover shadow-hover-primary'
          : 'border-border-strong',
      ].join(' ')}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-white [&>svg]:size-5 [&>svg]:text-icon-default"
        >
          {icon}
        </span>
        <span
          id={`${id}-label`}
          className="flex-1 min-w-0 text-xl leading-7 text-foreground"
        >
          {label}
        </span>
        <span
          aria-hidden
          className={[
            'flex size-7 shrink-0 items-center justify-center rounded-full border bg-white',
            selected ? 'border-primary-hover' : 'border-border-strong',
          ].join(' ')}
        >
          {selected && <span className="size-3 rounded-full bg-primary" />}
        </span>
      </div>
      <ul
        id={`${id}-checks`}
        className="flex flex-col gap-2"
      >
        {checks.map((check) => (
          <li
            key={check}
            className="flex items-start gap-2"
          >
            <Check
              aria-hidden
              className="size-5 shrink-0 text-icon-primary"
            />
            <span className="flex-1 min-w-0 text-base leading-6 text-muted-foreground">
              {check}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
